import { useEffect, useRef, useState } from "react";
import {
  ArrowDownToLine,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronRight,
  ImagePlus,
  LoaderCircle,
  Palette,
  RefreshCw,
  Shirt,
  Sparkles,
  Upload,
  WandSparkles,
  X,
  Zap,
} from "lucide-react";
import { garmentById, garments } from "../shared/garments";

type Concept = {
  id: string;
  model: "3000" | "64000" | "112r" | "gc102";
  color: string;
  designName: string;
  phrase: string;
  placement: string;
  prompt: string;
};

type Plan = {
  brand: { name: string; summary: string; tone: string[]; palette: string[] };
  concepts: Concept[];
};

type RenderState = { status: "queued" | "rendering" | "done" | "error"; image?: string; error?: string };
type Phase = "upload" | "analyzing" | "rendering" | "complete";

const initialHeroIndices = [
  garments.findIndex((item) => item.model === "3000" && item.color === "natural"),
  garments.findIndex((item) => item.model === "112r" && item.color === "black_white"),
  garments.findIndex((item) => item.model === "64000" && item.color === "military_green"),
];

const modelName: Record<Concept["model"], string> = {
  "3000": "Classic tee 3000",
  "64000": "Softstyle tee 64000",
  "112r": "Trucker cap 112R",
  "gc102": "Washed cap GC102",
};

async function parseJson(response: Response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "Something interrupted the studio. Try again.");
  return data;
}

export default function App() {
  const [logo, setLogo] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessContext, setBusinessContext] = useState("");
  const [phase, setPhase] = useState<Phase>("upload");
  const [plan, setPlan] = useState<Plan | null>(null);
  const [renders, setRenders] = useState<Record<string, RenderState>>({});
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [heroSlots, setHeroSlots] = useState({ current: initialHeroIndices, previous: [null, null, null] as (number | null)[] });
  const inputRef = useRef<HTMLInputElement>(null);
  const studioRef = useRef<HTMLDivElement>(null);
  const heroCursor = useRef(23);

  useEffect(() => () => { if (logoUrl) URL.revokeObjectURL(logoUrl); }, [logoUrl]);
  useEffect(() => {
    garments.forEach((garment) => { const preload = new Image(); preload.src = garment.image; });
  }, []);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setHeroSlots(({ current }) => {
        const next = [...current];
        const slot = heroCursor.current % 3;
        let candidate = (heroCursor.current * 17 + 11) % garments.length;
        while (next.includes(candidate)) candidate = (candidate + 1) % garments.length;
        next[slot] = candidate;
        heroCursor.current += 1;
        return { current: next, previous: current };
      });
    }, 3200);
    return () => window.clearInterval(timer);
  }, []);

  const chooseLogo = (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return setError("Choose a PNG, JPG, or WebP image.");
    if (file.size > 8 * 1024 * 1024) return setError("Your logo needs to be under 8 MB.");
    if (logoUrl) URL.revokeObjectURL(logoUrl);
    setLogo(file);
    setLogoUrl(URL.createObjectURL(file));
    setError("");
  };

  const generateOne = async (concept: Concept) => {
    if (!logo) return;
    setRenders((current) => ({ ...current, [concept.id]: { status: "rendering" } }));
    try {
      const body = new FormData();
      body.append("logo", logo);
      body.append("model", concept.model);
      body.append("color", concept.color);
      body.append("prompt", concept.prompt);
      const result = await parseJson(await fetch("/api/generate", { method: "POST", body }));
      setRenders((current) => ({ ...current, [concept.id]: { status: "done", image: result.image } }));
    } catch (reason) {
      setRenders((current) => ({
        ...current,
        [concept.id]: { status: "error", error: reason instanceof Error ? reason.message : "Generation failed." },
      }));
    }
  };

  const runQueue = async (concepts: Concept[]) => {
    for (let i = 0; i < concepts.length; i += 3) {
      await Promise.all(concepts.slice(i, i + 3).map(generateOne));
    }
    setPhase("complete");
  };

  const createCollection = async () => {
    if (!logo) return inputRef.current?.click();
    setError("");
    setPhase("analyzing");
    setPlan(null);
    setRenders({});
    requestAnimationFrame(() => studioRef.current?.scrollIntoView({ behavior: "smooth" }));
    try {
      const body = new FormData();
      body.append("logo", logo);
      body.append("businessName", businessName);
      body.append("businessContext", businessContext);
      const data = await parseJson(await fetch("/api/analyze", { method: "POST", body })) as Plan;
      const validConcepts = data.concepts.filter((concept) => garmentById(concept.model, concept.color));
      if (validConcepts.length !== 6) throw new Error("The plan selected an unavailable garment color. Please run it again.");
      const nextPlan = { ...data, concepts: validConcepts };
      setPlan(nextPlan);
      setRenders(Object.fromEntries(validConcepts.map((concept) => [concept.id, { status: "queued" }])));
      setPhase("rendering");
      await runQueue(validConcepts);
    } catch (reason) {
      setPhase("upload");
      setError(reason instanceof Error ? reason.message : "We couldn't start this collection.");
    }
  };

  const reset = () => {
    setPhase("upload");
    setPlan(null);
    setRenders({});
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const doneCount = Object.values(renders).filter((item) => item.status === "done").length;
  const isWorking = phase === "analyzing" || phase === "rendering";

  return (
    <main>
      <header className="topbar">
        <button className="wordmark" onClick={reset} aria-label="Pressmark home">
          <span className="wordmark-stamp"><Shirt size={18} strokeWidth={2.4} /></span>
          <span>PRESSMARK</span>
        </button>
        <div className="topbar-right">
          <span className="edition">BLIONES MERCH STUDIO</span>
          {phase !== "upload" && <button className="text-button" onClick={reset}>New collection <ArrowRight size={14} /></button>}
        </div>
      </header>

      {phase === "upload" ? (
        <>
          <section className="hero">
            <div className="hero-copy">
              <div className="eyebrow"><span>AI ART DIRECTION</span><span className="eyebrow-line" /></div>
              <h1>Your logo.<br /><em>Six pieces</em> people<br />actually want.</h1>
              <p className="hero-intro">Upload a restaurant logo. Pressmark reads the brand, chooses the right garments, and builds a pitch-ready merch collection in minutes.</p>
              <div className="hero-meta">
                <span><Check size={15} /> 2 tees · 2 caps</span>
                <span><Check size={15} /> 6 original concepts</span>
                <span><Check size={15} /> Ready to download</span>
              </div>
            </div>

            <div className="hero-board" aria-hidden="true">
              <div className="board-note">THE FIRST DROP<br /><span>starts here</span></div>
              {heroSlots.current.map((garmentIndex, index) => {
                const garment = garments[garmentIndex];
                const previousIndex = heroSlots.previous[index];
                const previous = previousIndex === null ? null : garments[previousIndex];
                const changing = previous !== null && previous.image !== garment.image;
                return (
                <div className={`hero-card hero-card-${index + 1}`} key={`hero-slot-${index}`}>
                  <div className="hero-card-photo">
                    <img src={garment.image} alt="" />
                    {changing && <img className="garment-leaving" src={previous!.image} alt="" />}
                  </div>
                  <span>{garment.kind.toUpperCase()} / {garment.model.toUpperCase()}</span>
                  {changing && (
                    <span className="garment-leaving-caption" key={previous!.image}>{previous!.kind.toUpperCase()} / {previous!.model.toUpperCase()}</span>
                  )}
                </div>
              )})}
              <div className="orbit orbit-one" /><div className="orbit orbit-two" />
              <Sparkles className="board-spark" size={29} />
            </div>
          </section>

          <section className="upload-section" id="create">
            <div className="section-heading">
              <span className="step-number">01</span>
              <div><p className="kicker">START WITH THE MARK</p><h2>Drop in your logo</h2></div>
              <p>Clean PNG, JPG, or WebP works best.<br />Maximum file size 8 MB.</p>
            </div>

            <div className="upload-workbench">
              <button
                type="button"
                className={`dropzone ${dragging ? "is-dragging" : ""} ${logo ? "has-logo" : ""}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => { event.preventDefault(); setDragging(false); chooseLogo(event.dataTransfer.files[0]); }}
              >
                <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => chooseLogo(e.target.files?.[0])} />
                {logoUrl ? (
                  <>
                    <span className="logo-preview"><img src={logoUrl} alt="Uploaded business logo" /></span>
                    <span className="drop-copy"><strong>{logo?.name}</strong><small>Click or drop to replace</small></span>
                    <span className="ready-badge"><CheckCircle2 size={15} /> Ready</span>
                  </>
                ) : (
                  <>
                    <span className="upload-icon"><ImagePlus size={27} /></span>
                    <span className="drop-copy"><strong>Drop your logo here</strong><small>or click to browse your files</small></span>
                    <span className="file-types">PNG · JPG · WEBP</span>
                  </>
                )}
              </button>

              <div className="launch-panel">
                <label htmlFor="business-name">Restaurant name <span>optional</span></label>
                <input id="business-name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. Sunday Service" maxLength={80} />
                <label htmlFor="business-context">What do you serve? <span>optional</span></label>
                <input id="business-context" value={businessContext} onChange={(e) => setBusinessContext(e.target.value)} placeholder="e.g. smash burgers and fries" maxLength={160} />
                <button className="primary-button" onClick={createCollection} disabled={!logo}>
                  <WandSparkles size={18} /> Create my collection <ArrowRight size={18} />
                </button>
                <p><Zap size={13} /> Built with GPT-5.6 Luna + GPT Image 2</p>
              </div>
            </div>
            {error && <div className="error-banner" role="alert"><X size={17} /> {error}</div>}
          </section>

          <section className="how-it-works">
            <p className="kicker">FROM MARK TO MERCH</p>
            <div className="process-row">
              <article><Palette size={22} /><span>01</span><h3>We read the brand</h3><p>Color, character, contrast, and visual cues.</p></article>
              <ChevronRight className="process-arrow" />
              <article><Shirt size={22} /><span>02</span><h3>We style the blanks</h3><p>Six colorways selected from real garment stock.</p></article>
              <ChevronRight className="process-arrow" />
              <article><Sparkles size={22} /><span>03</span><h3>We create the drop</h3><p>Six coordinated designs, rendered one by one.</p></article>
            </div>
          </section>
        </>
      ) : (
        <section className="studio" ref={studioRef}>
          <div className="studio-header">
            <div>
              <p className="kicker">LIVE ART DIRECTION</p>
              <h1>{plan ? `${plan.brand.name} / First drop` : "Reading your brand"}</h1>
            </div>
            <div className="studio-counter"><span>{phase === "analyzing" ? "—" : doneCount}</span><small>OF 6 RENDERED</small></div>
          </div>

          <div className="progress-rail" aria-live="polite">
            <ProgressStep icon={<Upload />} label="Logo received" state="done" />
            <ProgressStep icon={<Palette />} label="Brand + colors" state={phase === "analyzing" ? "active" : "done"} />
            <ProgressStep icon={<WandSparkles />} label="Design directions" state={phase === "analyzing" ? "waiting" : "done"} />
            <ProgressStep icon={<Sparkles />} label={phase === "complete" ? "Collection ready" : "Rendering mockups"} state={phase === "rendering" ? "active" : phase === "complete" ? "done" : "waiting"} />
          </div>

          {phase === "analyzing" && <AnalysisStage logoUrl={logoUrl} />}

          {plan && (
            <div className="studio-layout">
              <aside className="brand-ticket">
                <div className="ticket-logo"><img src={logoUrl} alt="Brand logo" /></div>
                <p className="ticket-label">SELECTED COLORWAYS</p>
                <h2>{plan.brand.name}</h2>
                <p className="colorway-intro">Six garment colors chosen to complement the logo.</p>
                <div className="selected-colorways" aria-label="Selected garment colors">
                  {plan.concepts.map((concept, index) => {
                    const selected = garmentById(concept.model, concept.color);
                    return <div className="selected-color" key={concept.id}>
                      <span className="selected-swatch" style={{ backgroundColor: selected?.hex }} />
                      <span><strong>{selected?.label}</strong><small>{selected?.kind === "tee" ? `Tee ${concept.model}` : `Cap ${concept.model.toUpperCase()}`}</small></span>
                    </div>;
                  })}
                </div>
                <div className="ticket-footer"><span>PM–DROP 001</span><span>6 PIECES</span></div>
              </aside>

              <div className="collection-grid">
                {plan.concepts.map((concept, index) => (
                  <MockupCard
                    key={concept.id}
                    concept={concept}
                    index={index}
                    state={renders[concept.id] || { status: "queued" }}
                    onRetry={() => generateOne(concept)}
                  />
                ))}
              </div>
            </div>
          )}

          {phase === "complete" && (
            <div className="completion-bar">
              <div><CheckCircle2 /><span><strong>Your first drop is ready.</strong><small>Download each concept or start another collection.</small></span></div>
              <button className="secondary-button" onClick={reset}><RefreshCw size={16} /> New collection</button>
            </div>
          )}
        </section>
      )}

      <footer><span>PRESSMARK®</span><p>Good food deserves good merch.</p><span>BLIONES STUDIO / 2026</span></footer>
    </main>
  );
}

function ProgressStep({ icon, label, state }: { icon: React.ReactNode; label: string; state: "done" | "active" | "waiting" }) {
  return <div className={`progress-step ${state}`}><span className="progress-icon">{state === "done" ? <Check /> : icon}</span><span>{label}</span>{state === "active" && <LoaderCircle className="spin" />}</div>;
}

function AnalysisStage({ logoUrl }: { logoUrl: string }) {
  return (
    <div className="analysis-stage">
      <div className="scanner">
        <img src={logoUrl} alt="Logo being analyzed" />
        <span className="scan-line" /><i className="corner corner-a" /><i className="corner corner-b" /><i className="corner corner-c" /><i className="corner corner-d" />
      </div>
      <div className="analysis-copy"><span className="pulse-dot" /><p>ANALYZING BRAND DNA</p><h2>Finding the colors<br />that make your mark pop.</h2><small>Reading contrast, palette, personality, and restaurant cues…</small></div>
      <div className="analysis-orbits"><Palette /><Shirt /><Sparkles /></div>
    </div>
  );
}

function MockupCard({ concept, state, index, onRetry }: { concept: Concept; state: RenderState; index: number; onRetry: () => void }) {
  const garment = garmentById(concept.model, concept.color);
  const number = String(index + 1).padStart(2, "0");
  const download = () => {
    if (!state.image) return;
    const link = document.createElement("a");
    link.href = state.image;
    link.download = `${concept.designName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.webp`;
    link.click();
  };
  return (
    <article className={`mockup-card status-${state.status}`}>
      <div className="mockup-media">
        <img src={state.image || garment?.image} alt={state.image ? `${concept.designName} merchandise concept` : `${garment?.label} blank garment`} />
        {state.status === "queued" && <div className="blank-status queued-status"><span>{number}</span><p>BLANK READY</p></div>}
        {state.status === "rendering" && <><span className="print-scan" /><div className="blank-status live-status"><LoaderCircle className="spin" /><span><p>CREATING DESIGN</p><small>The blank stays visible</small></span><div className="render-bar"><i /></div></div></>}
        {state.status === "error" && <div className="media-state error-state"><X /><p>RENDER PAUSED</p><small>{state.error}</small><button onClick={onRetry}><RefreshCw size={14} /> Try again</button></div>}
        {state.status === "done" && <button className="download-button" onClick={download} aria-label={`Download ${concept.designName}`}><ArrowDownToLine size={17} /></button>}
      </div>
      <div className="mockup-info">
        <div><span className="concept-number">CONCEPT {number}</span><h3>{concept.designName}</h3></div>
        <div className="garment-meta"><span className="color-dot" style={{ backgroundColor: garment?.hex }} /><span>{garment?.label}</span><i /> <span>{modelName[concept.model]}</span></div>
        <p className="concept-phrase">“{concept.phrase}”</p>
        <p>{concept.placement}</p>
      </div>
    </article>
  );
}
